<template>
  <div class="commentGrid" :class="{ isFullList }">
    <template v-for="(comment, commentIndex) in comments" :key="`date-${commentIndex}`">
      <div v-if="isFullList">
        <div class="date">
          {{ getDate(comment.date) }}
        </div>
        <div class="pr-3 white-space-nowrap">{{ comment.user }}</div>
      </div>
      <template v-else>
        <div class="date">
          {{ getDate(comment.date) }}
        </div>
        <div class="pr-3 white-space-nowrap">| {{ comment.user }}</div>
      </template>
      <div v-if="dirtyWordWrapFix" :key="`text-${commentIndex}`" class="breakable mr-3">
        <div v-for="(change, index) in comment.changes" :key="`change-${index}`">
          <span class="text-info mr-1"
            ><template v-if="change.noTranslate === true">{{ change.field }}</template
            ><template v-else>{{
              t(`components.TaskModal.columns.${change.field.toLowerCase()}`)
            }}</template></span
          >
          <span class="white-space-pre">
            [ <span class="text-muted">{{ change.from }} </span>
            <i class="fa fa-arrow-right fa-xs" /> {{ change.to }} ]</span
          >
        </div>
        <div
          v-for="(commentText, commentTextIndex) in comment.userComments"
          :key="`comment-${commentTextIndex}`"
        >
          <span :class="{ 'font-bold': isFullList }" class="white-space-pre">{{
            commentText
          }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, type PropType, ref } from "vue";
import { DateTime } from "luxon";
import { LISA_DATE_SHORT } from "@/language/translation";
import { useTranslation } from "@/language/translation";
import type { Comments } from "./useRevisions";

const props = defineProps({
  comments: {
    type: Array as PropType<Comments>,
    required: true,
  },
  isFullList: {
    type: Boolean,
    default: false,
  },
});
// The word wrap of TaskRevision is broken in a grid, we need to trigger a rerender here

const dirtyWordWrapFix = ref(!props.isFullList);

if (props.isFullList) {
  onMounted(async () => {
    await new Promise<void>((res) => {
      setTimeout(() => {
        res();
      }, 1 /* ms */);
    });
    dirtyWordWrapFix.value = true;
  });
}

const { locale } = useTranslation();
function getDate(date: DateTime) {
  return props.isFullList
    ? date.setLocale(locale.value).toLocaleString({
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : date.setLocale(locale.value).toLocaleString(LISA_DATE_SHORT);
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.commentGrid {
  display: grid;
  gap: 4px;
  grid-template-columns: min-content max-content auto;
  align-items: baseline;

  &.isFullList {
    max-height: 180px;
    overflow-y: auto;
    grid-template-columns: max-content auto;
  }

  .breakable {
    min-width: 0;
    min-height: 0;
    word-wrap: break-word;
  }

  .date {
    white-space: nowrap;
  }
}
</style>
